import type { ReactNode } from "react";
import { MessageOutlined, PhoneOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../../api/reservations";
import { DiningTableIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import { formatClock } from "../../logic/dayTime";
import { reservationEndMs, reservationStartMs } from "../../logic/tableStatus";

/**
 * Резервацията в „Настани" на резервация с няколко маси — карта-билет като
 * в Маркиране (ordering/…/seat/SeatReservationTicket.tsx), в цветовете на
 * модула: горе часът и колко трае, под перфорацията — гостите, телефонът и
 * масите („Маси 25, 29") с иконки, най-долу бележката в отделно каре. `compact` — на
 * телефон и таблет, над избора: часът и гостите в горната лента.
 */
const SeatReservationTicket = ({
  reservation,
  tableNames,
  compact = false,
}: {
  reservation: Reservation;
  // Различните маси на резервацията (по реда ѝ).
  tableNames: string[];
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const guests = t("reservations.counts.guests", { count: reservation.guests });

  return (
    <div className="overflow-hidden rounded-2xl border border-shade-2 bg-surface">
      <div className={`flex items-center justify-between gap-3 bg-brand-tint ${compact ? "px-3.5 py-2.5" : "px-4 py-3.5"}`}>
        <span className="flex min-w-0 items-baseline gap-1 font-bold leading-none tabular-nums text-ink">
          <span className={compact ? "text-[18px]" : "text-[24px] tracking-[-0.5px]"}>
            {formatClock(reservationStartMs(reservation))}
          </span>
          <span className="text-[14px] text-body">–{formatClock(reservationEndMs(reservation))}</span>
        </span>
        <span className="shrink-0 text-[12.5px] font-bold text-brand-deep-ink">
          {compact ? guests : format.duration(reservation.durationMinutes)}
        </span>
      </div>

      {/* Перфорацията на билета — прорезите са в цвета на колоната зад картата. */}
      <div className="relative h-0 border-t border-dashed border-shade-3">
        <span className="absolute -left-2 -top-2 size-4 rounded-full border border-shade-2 bg-panel" />
        <span className="absolute -right-2 -top-2 size-4 rounded-full border border-shade-2 bg-panel" />
      </div>

      <div className={`flex flex-col ${compact ? "gap-2 px-3.5 py-3" : "gap-2.5 px-4 py-4"}`}>
        {!compact && <TicketRow icon={<TeamOutlined />}>{guests}</TicketRow>}
        {reservation.phone && (
          <TicketRow icon={<PhoneOutlined />}>
            <span className="tabular-nums">{reservation.phone}</span>
          </TicketRow>
        )}
        {/* Масите — текст като другите редове, иконата е маса отстрани. */}
        <TicketRow icon={<DiningTableIcon className="size-4" strokeWidth={1.8} />}>
          {t("seatReservation.tables", { count: tableNames.length, tables: tableNames.join(", ") })}
        </TicketRow>
        {reservation.note && (
          <div className="mt-1 flex items-start gap-2 rounded-xl bg-brand-tint px-3 py-2.5 text-[13px] font-semibold leading-[1.4] text-brand-deep-ink">
            <MessageOutlined className="mt-[3px] shrink-0" />
            <span className="min-w-0 break-words">{reservation.note}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/** Ред от билета: иконка в плочка + стойност. */
const TicketRow = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <div className="flex min-w-0 items-center gap-2.5 text-[14px] font-semibold text-ink">
    <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-fill text-[13px] text-body">
      {icon}
    </span>
    <span className="min-w-0 break-words">{children}</span>
  </div>
);

export default SeatReservationTicket;
